import React, { useState, useCallback, useEffect } from 'react';
import { Icon } from '../../../pages/DesignSystem';
import { useOrganizer } from '../../../contexts/OrganizerContext';
import { OrganizerProfile } from '../../../types/organizer.types';
import ProfileEditForm from '../../../pages/OrganizerProfile/components/ProfileEditForm';
import ProfileLivePreview from './ProfileLivePreview';
import './ProfileEditModal.css';

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

/**
 * ProfileEditModal - Desktop modal for editing organizer profile
 * Split layout: Form on left, Live Preview on right
 */
const ProfileEditModal: React.FC<ProfileEditModalProps> = ({
  isOpen,
  onClose,
  onSubmitSuccess
}) => {
  const { organizerProfile, updateProfile } = useOrganizer();

  // Track form data for live preview
  const [previewData, setPreviewData] = useState<Partial<OrganizerProfile>>({});

  // Initialize preview data with current profile
  useEffect(() => {
    if (organizerProfile && isOpen) {
      setPreviewData(organizerProfile);
    }
  }, [organizerProfile, isOpen]);

  // Handle form data changes for live preview
  const handleFormDataChange = useCallback((data: Partial<OrganizerProfile>) => {
    setPreviewData(prev => ({ ...prev, ...data }));
  }, []);

  // Handle save
  const handleSave = useCallback(async (updatedProfile: OrganizerProfile) => {
    try {
      await updateProfile(updatedProfile);
      onSubmitSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  }, [updateProfile, onSubmitSuccess, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key and body scroll lock
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="profile-edit-modal__backdrop" onClick={handleBackdropClick}>
      <div className="profile-edit-modal__container">
        {/* Modal Header */}
        <header className="profile-edit-modal__header">
          <h2 className="profile-edit-modal__title">Edit Profile</h2>
          <button
            className="profile-edit-modal__close"
            onClick={onClose}
            type="button"
            aria-label="Close"
          >
            <Icon name="X" size={24} />
          </button>
        </header>

        {/* Modal Content - Split Layout */}
        <div className="profile-edit-modal__content">
          {/* Left Side - Form */}
          <div className="profile-edit-modal__form-section">
            <ProfileEditForm
              profile={organizerProfile}
              onClose={onClose}
              onSave={handleSave}
              onFormDataChange={handleFormDataChange}
              mode="modal"
            />
          </div>

          {/* Right Side - Live Preview */}
          <div className="profile-edit-modal__preview-section">
            <ProfileLivePreview formData={previewData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileEditModal;
